/* Common Imports */

import { styled } from "@mui/system";

/* Redux & Type Imports */

import { NextRouter } from "next/router";

/* Component Imports */

import { Typography } from "@mui/material";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import BeegruButton from "../../common-components/buttons/BeegruButton";

/* Icon Imports */

import { CallOutlined, Done } from "@mui/icons-material";

/* Styled Components */

const MainContainer = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	height: "100%",
	minHeight: "100%",
	flex: 1,
	position: "relative",
	background: "#1e7b58",
	backgroundSize: "cover",
	backgroundPosition: "center",
	backgroundRepeat: "no-repeat",
	backgroundImage: "url('/images/broadcast/confirmedScreenBackground.svg')",
});

const HeaderContainer = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "1rem 1.5rem 1.5rem 1.5rem",
	gap: "1rem",
	backgroundSize: "cover",
	backgroundPosition: "center",
	backgroundRepeat: "no-repeat",
	backgroundImage: "url('/images/broadcast/confirmedScreenBackground.svg')",
});

const SuccessText = styled(Typography)({
	fontWeight: 700,
	color: "#FFFFFF",
	fontSize: "1.5rem",
	textAlign: "center",
});

const Card = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	background: "#fff",
	borderRadius: "1rem 1rem 0rem 0rem",
	padding: "2rem 1.5rem 1rem 1.5rem",
});

const CardHeader = styled("div")({
	display: "flex",
	alignItems: "center",
	gap: 10,
	marginBottom: 16,
	width: "100%",
	justifyContent: "space-between",
});

const CardTitle = styled(Typography)({
	fontWeight: 700,
	fontSize: 20,
	color: "#424242",
	display: "flex",
	alignItems: "center",
	gap: 8,
});

const List = styled("ul")({
	listStyle: "none",
	padding: 0,
	margin: 0,
	width: "100%",
	paddingTop: "0.3rem",
});

const ListItem = styled("li")({
	display: "flex",
	alignItems: "flex-start",
	gap: 10,
	fontSize: 16,
	color: "#424242",
	marginBottom: 18,
});

const CheckIcon = styled(Done)({
	color: "#003F7D",
	fontSize: "20px",
	marginTop: 2,
});

export default function SuccessScreen({ close, router }: { close: Function; router: NextRouter }) {
	/* Translations */

	const localLang = {
		days: router.locale === "ar-AE" ? "يوم " : router.locale === "kn-IN" ? " ದಿನಗಳು" : " days",
		your_broadcast_has_been_created_successfully:
			router.locale === "ar-AE"
				? "تم إنشاء البث الخاص بك بنجاح"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಪ್ರಸಾರ ಯಶಸ್ವಿಯಾಗಿ ರಚಿಸಲಾಗಿದೆ"
					: "Your broadcast has been created successfully",
		contact_support:
			router.locale === "ar-AE" ? "اتصل بالدعم" : router.locale === "kn-IN" ? "ಸಹಾಯವನ್ನು ಸಂಪರ್ಕಿಸಿ" : "Contact Support",
		heres_what_to_expect_next:
			router.locale === "ar-AE"
				? "إليك ما يمكن توقعه بعد ذلك"
				: router.locale === "kn-IN"
					? "ಮುಂದೆ ಏನು ನಿರೀಕ್ಷಿಸಬಹುದು ಎಂಬುದನ್ನು ಇಲ್ಲಿ ನೋಡಿ"
					: "Here's what to expect next",
		pointer_one:
			router.locale === "ar-AE"
				? "الوكلاء المهتمون سيقبلون بثك"
				: router.locale === "kn-IN"
					? "ಆಸಕ್ತಿ ಹೊಂದಿರುವ ಏಜೆಂಟ್‌ಗಳು ನಿಮ್ಮ ಪ್ರಸಾರವನ್ನು ಸ್ವೀಕರಿಸುತ್ತಾರೆ"
					: "Interested agents will accept your broadcast",
		pointer_two:
			router.locale === "ar-AE"
				? "بمجرد القبول، يتم فتح دردشة فردية تتيح التواصل المباشر"
				: router.locale === "kn-IN"
					? "ಒಮ್ಮೆ ಸ್ವೀಕರಿಸಿದ ನಂತರ, 1-1 ಚಾಟ್ ತೆರೆಯಲ್ಪಡುತ್ತದೆ, ಇದು ನೇರ ಸಂವಹನವನ್ನು ಅವಕಾಶ ಮಾಡಿಕೊಡುತ್ತದೆ"
					: "Once accepted, a 1-1 chat opens, allowing direct communication",
		pointer_three:
			router.locale === "ar-AE"
				? "يمكنك مشاركة المزيد من التفاصيل والتنسيق مباشرة عبر الدردشة أو الاتصال"
				: router.locale === "kn-IN"
					? "ನೀವು ಹೆಚ್ಚಿನ ವಿವರಗಳನ್ನು ಹಂಚಿಕೊಳ್ಳಬಹುದು ಮತ್ತು ಚಾಟ್ ಅಥವಾ ಕರೆ ಮೂಲಕ ನೇರವಾಗಿ ಸಂಯೋಜಿಸಬಹುದು"
					: "You can share more details and coordinate directly through the chat or call",
		pointer_four:
			router.locale === "ar-AE"
				? "تتبّع جميع البثوث المرسلة من لوحة التحكم -> البثوث"
				: router.locale === "kn-IN"
					? " ಡ್ಯಾಶ್‌ಬೋರ್ಡ್ -> ಪ್ರಸಾರಗಳಲ್ಲಿ ನಿಮ್ಮ ಕಳುಹಿಸಿದ ಎಲ್ಲಾ ಪ್ರಸಾರಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ"
					: "Track all your sent broadcasts from Dashboard -> Broadcasts",
	};

	return (
		<MainContainer>
			{/* Header */}

			<HeaderContainer>
				{/* <SvgIcon
					component={BroadcastIconWithEffect}
					inheritViewBox={false}
					viewBox="-20 00 230 150"
					sx={{
						width: "220px",
						height: "200px",
					}}
				/> */}

				<DotLottieReact
					src="/images/lotties/broadcast/broadcast.lottie"
					autoplay
					loop
					style={{
						width: "40rem",
						height: "20rem",
					}}
				/>

				<SuccessText variant="h5">{localLang.your_broadcast_has_been_created_successfully}</SuccessText>

				<BeegruButton
					size="small"
					flavor="primary"
					variant="contained"
					disableRipple
					disableFocusRipple
					disableTouchRipple
					href="tel:+917829371999"
					sx={{
						"&.MuiButton-contained": {
							background: "#ffb547",
							color: "#621b16",
							fontWeight: 600,
							padding: "0.5rem 1rem",
							borderRadius: "2rem",
							boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
							"&:hover": {
								background: "#ffb547",
								boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
							},
							"&:active": {
								background: "#ffb547",
								boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
							},
							"&:focus": {
								background: "#ffb547",
								boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
							},
						},
					}}
				>
					<CallOutlined
						sx={{
							fontSize: "1rem",
							color: "#621B16",
							margin: "0rem 0.5rem 0rem 0rem",
						}}
					/>
					{localLang.contact_support}
				</BeegruButton>
			</HeaderContainer>

			{/* What to expect card */}

			<Card>
				<CardHeader>
					<CardTitle variant="h6">{localLang.heres_what_to_expect_next}</CardTitle>
				</CardHeader>

				<List>
					<ListItem>
						<CheckIcon />
						{localLang.pointer_one}
					</ListItem>
					<ListItem>
						<CheckIcon />
						{localLang.pointer_two}
					</ListItem>
					<ListItem>
						<CheckIcon />
						{localLang.pointer_three}
					</ListItem>
					<ListItem>
						<CheckIcon />
						{localLang.pointer_four}
					</ListItem>
				</List>
			</Card>
		</MainContainer>
	);
}
